import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useHistory, useParams } from 'react-router-dom';
import AcademyPlayer, { canDetectVideoEnd } from 'components/atoms/academy_player';
import AmbassadorBadge from 'components/atoms/ambassador_badge';
import EmptyState from 'components/atoms/empty_state';
import PillButton from 'components/atoms/pill_button';
import { hasCookie, setCookie } from 'services/actions/cookies_actions';
import {
  ambassadorFeedbackPath,
  fetchAmbassadorProgress,
  findAmbassadorToolState,
  formatAmbassadorDate,
  recordAmbassadorProgress,
  startAmbassadorTest,
} from 'services/ambassador';
import { AMBASSADOR_TEST_DAYS, findAmbassadorTool } from 'services/ambassador_tools';
import { track } from 'services/tracking';
import { ApplicationState } from 'store';
import { refreshProfile } from 'store/modules/profile_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

const STEPS = [
  {
    title: 'Aprenda sobre a ferramenta',
    text: 'Veja o vídeo explicativo da ferramenta e entenda como ela pode ser usada na sua rotina licitatória.',
  },
  {
    title: 'Utilize a ferramenta no seu dia a dia',
    text: 'Veja como funciona no dia a dia, explore as funcionalidades e entenda melhor a ferramenta.',
  },
  {
    title: 'Compartilhe a sua experiência',
    text: 'Converse com nosso time de executivos para compartilhar como foi o uso da ferramenta.',
  },
];

const watchedCookie = (slug: string, userId: number) => `AmbassadorVideoWatched_${slug}_${userId}`;

type StartStatus = 'idle' | 'starting' | 'error';

const AmbassadorToolDetail: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { slug } = useParams<{ slug: string }>();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isAmbassador = Boolean(profile.ambassador?.member);
  const tool = findAmbassadorTool(slug);
  const toolState = findAmbassadorToolState(profile.ambassador?.tools, slug);
  const subscriber = profile.ambassador?.subscriber === true;
  const testUntil = toolState.test_until ? formatAmbassadorDate(toolState.test_until) : '';
  const [endedSlug, setEndedSlug] = useState<string | null>(null);
  const [serverCheck, setServerCheck] = useState<{ slug: string; watched: boolean } | null>(null);
  const [startStatus, setStartStatus] = useState<StartStatus>('idle');

  // O vídeo só trava o primeiro "Iniciar teste". Sem vídeo, ou com um player
  // que não avisa o fim, não há o que esperar.
  const mustWatch =
    toolState.status === 'available' && Boolean(tool?.embedUrl && canDetectVideoEnd(tool.embedUrl));
  const cookieWatched = hasCookie(watchedCookie(slug, profile.id));
  const serverWatched = serverCheck?.slug === slug && serverCheck.watched;
  const watched = endedSlug === slug || cookieWatched || serverWatched;
  const locked = mustWatch && !watched;
  const checking = locked && serverCheck?.slug !== slug;

  useEffect(() => {
    document.title = tool ? `${tool.name} - Embaixadores` : 'Embaixadores - ConLicitação';
  }, [tool]);

  // O cookie só existe no navegador em que o vídeo foi assistido; sem ele, o
  // servidor diz se a pessoa já assistiu em outro aparelho.
  useEffect(() => {
    if (!isAmbassador || !mustWatch || cookieWatched) return undefined;

    let active = true;
    fetchAmbassadorProgress(slug).then((progress) => {
      if (!active) return;

      const seen = Boolean(progress?.video_watched);
      if (seen) setCookie(watchedCookie(slug, profile.id), true);
      setServerCheck({ slug, watched: seen });
    });

    return () => {
      active = false;
    };
  }, [isAmbassador, mustWatch, cookieWatched, slug, profile.id]);

  const handleVideoEnded = useCallback(() => {
    setCookie(watchedCookie(slug, profile.id), true);
    setEndedSlug(slug);
    recordAmbassadorProgress(slug, 'video_watched');
    track('embaixador.concluiu_video', {}, slug);
  }, [slug, profile.id]);

  if (!isAmbassador || toolState.status === 'locked') return <Redirect to="/painel" />;

  if (!tool) {
    return (
      <Container className="page-container">
        <div className="tool-not-found">
          <EmptyState
            icon="search_off"
            title="Ferramenta não encontrada"
            description="Ela pode ter saído do programa de testes."
          />
        </div>
      </Container>
    );
  }

  // Sem link a ferramenta ainda não está pronta para o teste.
  const { link } = tool;
  if (!link) return <Redirect to="/painel" />;

  const startTest = async () => {
    if (locked || startStatus === 'starting') return;

    setStartStatus('starting');
    const started = await startAmbassadorTest(tool.slug);
    if (!started) {
      setStartStatus('error');
      return;
    }

    track('embaixador.iniciou_teste', {}, tool.slug);
    dispatch(refreshProfile());
    history.push(link);
  };

  const feedbackPath = ambassadorFeedbackPath(tool.slug);

  const startAction = () => {
    // Sem assinatura do boletim o teste não começa nem continua; o feedback segue aberto.
    if (!subscriber && toolState.status !== 'expired') {
      return (
        <>
          <p className="tool-start-notice" role="status">
            <i className="material-icons">info</i>O teste das ferramentas é exclusivo para
            assinantes do boletim.
          </p>
          {toolState.status === 'testing' && (
            <Link className="tool-feedback-link" to={feedbackPath}>
              Enviar feedback
            </Link>
          )}
        </>
      );
    }

    if (toolState.status === 'testing') {
      return (
        <>
          <p className="tool-start-hint">Teste liberado até {testUntil}.</p>
          <PillButton size="lg" to={link}>
            Continuar teste
          </PillButton>
          <Link className="tool-feedback-link" to={feedbackPath}>
            Enviar feedback
          </Link>
        </>
      );
    }

    if (toolState.status === 'expired') {
      return toolState.feedback_sent ? (
        <p className="tool-start-hint" role="status">
          O teste terminou em {testUntil}. Obrigado pelo feedback!
        </p>
      ) : (
        <>
          <p className="tool-start-hint" role="status">
            O teste terminou em {testUntil}. Envie o seu feedback para liberar a próxima ferramenta.
          </p>
          <PillButton size="lg" to={feedbackPath}>
            Enviar feedback
          </PillButton>
        </>
      );
    }

    // O botão só libera depois do vídeo: o prazo conta a partir do clique nele.
    if (locked) {
      return checking ? null : (
        <>
          <p className="tool-start-notice" role="status">
            <i className="material-icons">info</i>
            Assista ao vídeo até o fim para liberar o teste.
          </p>
          <PillButton size="lg" variant="muted" icon="lock" disabled>
            Iniciar teste
          </PillButton>
        </>
      );
    }

    return (
      <>
        {startStatus === 'error' && (
          <p className="tool-start-hint tool-start-error" role="alert">
            Não foi possível iniciar o teste agora. Tente novamente.
          </p>
        )}
        <PillButton size="lg" onClick={startTest} disabled={startStatus === 'starting'}>
          {startStatus === 'starting' ? 'Iniciando...' : 'Iniciar teste'}
        </PillButton>
        <p className="tool-start-hint">
          Ao iniciar, a ferramenta fica liberada para você por {AMBASSADOR_TEST_DAYS} dias.
        </p>
      </>
    );
  };

  return (
    <Container className="page-container">
      <main className="tool-main">
        <div className="tool-content">
          <div className="tool-heading">
            <h1>{tool.name}</h1>
            <AmbassadorBadge />
          </div>

          <div className="tool-video">
            {tool.embedUrl ? (
              <AcademyPlayer
                src={tool.embedUrl}
                title={`Vídeo explicativo: ${tool.name}`}
                onEnded={handleVideoEnded}
              />
            ) : (
              <div className="tool-video-empty">
                <i className="material-icons">ondemand_video</i>
                <span>O vídeo explicativo estará disponível em breve.</span>
              </div>
            )}
          </div>

          <p className="tool-description">{tool.description || tool.summary}</p>
        </div>
      </main>

      <aside className="tool-steps">
        <ol>
          {STEPS.map((step, index) => (
            <li key={step.title}>
              <span className="step-number">{index + 1}. Passo</span>
              <strong className="step-title">{step.title}</strong>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
        <div className="tool-start">{startAction()}</div>
      </aside>
    </Container>
  );
};

export default AmbassadorToolDetail;
